import type { State } from "@/pages/TriagePage/application/useSelection.types"
import type { AllIssuesInProject } from "@/pages/TriagePage/application/useTriage.types"
import type { Issue } from "."

export const isDismissed = (issue: Issue) => issue.status === "dismissed"

export const isOpen = (issue: Issue) => issue.status === "open"

export const isIssueForReopen = (issues: Issue[]) => issues.some(isDismissed)

export const isIssueForDismissed = (issues: Issue[]) => issues.some(isOpen)

export const getInitialState = () => ({
  issuesCount: 0,
  issuesTotal: 0,
  reOpen: false,
  dismiss: false,
  projectsCount: 0,
  allocatableIssueCount: 0,
  projectWithAllocatableIssuesCount: 0
})

export const getProjectIds = (selectedIssues: State["items"]) =>
  Object.keys(selectedIssues)

export const getStateFromSelectedIssues = (selectedState: State<Issue>) => {
  try {
    const isFlat = selectedState.selectedParentIds.length === 0
    const selectedIssues = selectedState.items
    const projectIds = getProjectIds(selectedIssues)

    return projectIds.reduce((acc, projectId) => {
      const projectState = selectedIssues[projectId] || {}

      if (projectState.updating) return acc

      if (
        !projectState.selectedState ||
        (!isFlat && projectState.selectedState === "none")
      )
        return acc

      const {
        selectedAllInProject,
        metadata,
        selectedNestedIds = [],
        nestedItems = []
      } = projectState
      let { issuesCount, issuesTotal, reOpen, dismiss, projectsCount } = acc
      const issueTotalInProject = (metadata || {}).issuesTotal || 0

      const selectedNestedItems = nestedItems.filter(({ id }) =>
        selectedNestedIds.includes(id)
      )

      if (selectedNestedIds.length) {
        issuesCount += selectedAllInProject
          ? issueTotalInProject
          : selectedNestedIds.length
        issuesTotal += issueTotalInProject
        projectsCount += 1
      }

      if (!acc.reOpen) {
        reOpen = isIssueForReopen(selectedNestedItems)
      }

      if (!acc.dismiss) {
        dismiss = isIssueForDismissed(selectedNestedItems)
      }

      const isAllocatableIssue =
        !selectedState.selectedAll &&
        !selectedAllInProject &&
        issueTotalInProject > nestedItems.length
      const nextAllocatableIssueCount = isAllocatableIssue
        ? acc.allocatableIssueCount +
          (issueTotalInProject - selectedNestedIds.length)
        : acc.allocatableIssueCount
      const nextProjectWithAllocatableIssuesCount = isAllocatableIssue
        ? acc.projectWithAllocatableIssuesCount + 1
        : acc.projectWithAllocatableIssuesCount

      return {
        issuesCount,
        issuesTotal,
        reOpen,
        dismiss,
        projectsCount,
        allocatableIssueCount: nextAllocatableIssueCount,
        projectWithAllocatableIssuesCount: nextProjectWithAllocatableIssuesCount
      }
    }, getInitialState())
  } catch {
    return null
  }
}

export const filterIssuesByProject = (
  selectedIssues: State<Issue>["items"],
  predicate: (issue: Issue) => boolean
) =>
  getProjectIds(selectedIssues).reduce(
    (acc, projectId) => {
      const {
        metadata,
        selectedAllInProject,
        nestedItems = [],
        selectedNestedIds = []
      } = selectedIssues[projectId] || {}

      if (selectedAllInProject && metadata) {
        acc[projectId] = { selectedAllInProject, projectId, metadata }

        return acc
      }

      const selectedNestedItems = nestedItems.filter(({ id }) =>
        selectedNestedIds.includes(id)
      )

      const issues = selectedNestedItems.filter(predicate)

      if (!issues?.length) return acc

      acc[projectId] = issues

      return acc
    },
    {} as Record<string, Issue[] | AllIssuesInProject>
  )

export const getIssuesForDismissByProject = (
  selectedIssues: State<Issue>["items"]
) => filterIssuesByProject(selectedIssues, isOpen)

export const getIssuesForReopenByProject = (
  selectedIssues: State<Issue>["items"]
) => filterIssuesByProject(selectedIssues, isDismissed)
